import React from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { format } from 'date-fns';
import { parseDateOnly, isSameYMD } from '../../utils/dateUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip);

const ChartContainer = ({ currentWeekData, fortnightStartDate }) => {
  const findWeekBoundaries = (data, startDate) => {
    const fortnightStart = parseDateOnly(startDate);
    return data.reduce((boundaries, item, index) => {
      const currentDate = parseDateOnly(item.date);
      const daysSinceStart = Math.floor((currentDate - fortnightStart) / (1000 * 60 * 60 * 24));
      if (daysSinceStart % 7 === 0 && index !== 0) {
        boundaries.push(index);
      }
      return boundaries;
    }, []);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
  };

  const formatDateSafely = (date) => {
    try {
      const dt = parseDateOnly(date);
      return format(dt, 'dd MMM');
    } catch (error) {
      console.error('Error parsing date:', error.name);
      return 'Invalid Date';
    }
  };

  const data = {
    labels: currentWeekData.map(d => formatDateSafely(d.date)),
    datasets: [
      {
        label: 'Balance',
        data: currentWeekData.map(d => ({
          x: formatDateSafely(d.date),
          y: d.endBalance,
          startBalance: d.startBalance,
          income: d.income,
          expenses: d.expenses,
          endBalance: d.endBalance,
          billDetails: d.billDetails || [],
          transferDetails: d.transferDetails || []
        })),
        borderColor: 'hsl(var(--primary))',
        tension: 0.1
      }
    ]
  };

  const options = {
    title: {
      display: false,
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        ticks: {
          callback: (value) => formatCurrency(value)
        }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const dataPoint = context.raw;
            const labels = [
              `Starting Balance: ${formatCurrency(dataPoint.startBalance)}`,
              `Pays: ${formatCurrency(dataPoint.income)}`,
              `Expenses: ${formatCurrency(dataPoint.expenses)}`,
              `Ending Balance: ${formatCurrency(dataPoint.endBalance)}`
            ];
            
            const todayLabel = context.label;

            // Bills section: list only today's bills; summarize earlier overdue
            if (dataPoint.billDetails && dataPoint.billDetails.length > 0) {
              labels.push('');
              const todaysBills = dataPoint.billDetails.filter(bill => {
                const dueStr = formatDateSafely(bill.due_date);
                const paidStr = bill.paid_date ? formatDateSafely(bill.paid_date) : null;
                // Consider paid bills to show on their paid date
                return (paidStr && paidStr === todayLabel) || dueStr === todayLabel;
              });
              const priorBills = dataPoint.billDetails.filter(bill => {
                const dueStr = formatDateSafely(bill.due_date);
                return dueStr !== todayLabel;
              });

              if (todaysBills.length > 0) {
                labels.push('Bills on this day:');
                todaysBills.forEach(bill => {
                  const status = bill.paid ? 'PAID' : 'DUE';
                  labels.push(`- ${bill.name}: ${formatCurrency(Math.abs(parseFloat(bill.amount)))} (${status})`);
                });
              }

              if (priorBills.length > 0) {
                const priorTotal = priorBills.reduce((sum, b) => sum + Math.abs(parseFloat(b.amount) || 0), 0);
                labels.push(`Previous overdue: ${formatCurrency(priorTotal)}`);
              }
            }

            // Transfers section (leave detailed listing for now)
            if (dataPoint.transferDetails && dataPoint.transferDetails.length > 0) {
              labels.push('');
              const todaysTransfers = [];
              const priorTransfers = [];
              dataPoint.transferDetails.forEach(tr => {
                const dueStr = formatDateSafely(tr.due_date || tr.next_pay || tr.date);
                const paidStr = tr.paid_date ? formatDateSafely(tr.paid_date) : null;
                const isPaid = !!(tr.transferMade || tr.paid);
                const effectiveDateStr = isPaid && paidStr ? paidStr : dueStr;
                const entry = `- ${tr.name || tr.nickname || 'Transfer'}: ${formatCurrency(Math.abs(parseFloat(tr.amount || tr.transferAmount || 0)))}` + (isPaid ? ' (PAID)' : ' (DUE)');
                if (effectiveDateStr === todayLabel) {
                  todaysTransfers.push(entry);
                } else {
                  priorTransfers.push(entry + (effectiveDateStr ? ` [${effectiveDateStr}]` : ''));
                }
              });
              if (todaysTransfers.length > 0) {
                labels.push('Transfers on this day:');
                todaysTransfers.forEach(l => labels.push(l));
              }
              if (priorTransfers.length > 0) {
                labels.push('Prior unpaid transfers:');
                priorTransfers.forEach(l => labels.push(l));
              }
            }
            
            return labels;
          }
        }
      },
      background: {
        beforeDraw(chart) {
          const ctx = chart.ctx;
          const xAxis = chart.scales.x;
          const yAxis = chart.scales.y;

          const weekBoundaries = findWeekBoundaries(currentWeekData, fortnightStartDate);

          weekBoundaries.forEach(index => {
            const labelAtBoundary = formatDateSafely(currentWeekData[index].date);
            const x = xAxis.getPixelForValue(labelAtBoundary);

            // Draw dulled background
            ctx.fillStyle = 'rgba(0,0,0,0.05)';
            ctx.fillRect(x, yAxis.top, xAxis.right - x, yAxis.bottom - yAxis.top);

            // Draw week boundary line
            ctx.strokeStyle = 'rgba(0,0,0,0.2)';
            ctx.setLineDash([5, 5]);
            ctx.beginPath();
            ctx.moveTo(x, yAxis.top);
            ctx.lineTo(x, yAxis.bottom);
            ctx.stroke();
            ctx.setLineDash([]);
          });
        }
      }
    }
  };

  return (
    <div style={{ height: '300px', width: '100%' }}>
      <Line data={data} options={options} />
    </div>
  );
};

export default ChartContainer;